import {
  AbilityBuilder,
  buildMongoQueryMatcher,
  createMongoAbility,
  subject as caslSubject,
} from "@casl/ability";
import {$and, $elemMatch, $not, $or, and, elemMatch, not, or} from "@ucast/mongo2js";
import {isEmpty, map, some} from "underscore";

export const ADMIN_ROLE_ID = 1;
export const ACCESS_PERMISSIONS = [
  {action: "access", subject: "Mobile"},
  {action: "access", subject: "Web"},
];

const ACTION_ALIASES = {
  read: [
    "read",
    "findUnique",
    "findUniqueOrThrow",
    "findFirst",
    "findFirstOrThrow",
    "findMany",
    "count",
    "aggregate",
    "groupBy",
  ],
  create: ["create", "createOne", "createMany", "createManyAndReturn"],
  update: ["update", "updateOne", "updateMany", "updateManyAndReturn"],
  delete: ["delete", "deleteOne", "deleteMany"],
};

const UNAUTHORIZED = {do: "unauthorized", on: "unauthorized"};

const buildConditions = (conditions, user) => {
  if (isEmpty(conditions)) {
    return;
  }

  const original = JSON.stringify(conditions);
  const substituted = original.replaceAll("\"#{userModel.id}\"", user ? user.id : -1);

  return JSON.parse(substituted);
};

const buildFields = (fields) => (isEmpty(fields) ? undefined : fields);

const resolveAction = (action) => {
  return ACTION_ALIASES[action] || action;
};

const grantSubject = (subject) => `Grant_${subject}`;
const permissionSubject = (subject) => `Permission_${subject}`;

export const abilityHasPermission = (ability, permission) =>
  ability.can(permission.action, permissionSubject(permission.subject));

const TEST_PERMISSION_SUBJECTS = [
  "ApiKey",
  "AuditSummary",
  "Dashboard",
  "DataOption",
  "Dataset",
  "DocumentLibrary",
  "Exercise",
  "HelpGuide",
  "Incident",
  "IncidentAuditLog",
  "IncidentSummary",
  "Integration",
  "Message",
  "News",
  "Notification",
  "Obligation",
  "Organization",
  "Permission",
  "Playbook",
  "RegulatoryResearch",
  "ReportTemplate",
  "ResponsePlan",
  "RexAIUtilities",
  "RiskAssessment",
  "Role",
  "Task",
  "TrustedAdvisor",
  "User",
];

export const createTestAbility = () => {
  const {can, rules} = new AbilityBuilder(createMongoAbility);
  can("manage", map(TEST_PERMISSION_SUBJECTS, permissionSubject));
  can("manage", grantSubject("Activity"));

  return createMongoAbility(rules);
};

export const createPermissionAbility = (permissions, grants, user) => {
  const {can, build} = new AbilityBuilder(createMongoAbility);

  for (const {action, subject} of permissions) {
    can(action, permissionSubject(subject));
  }

  for (const {action, subject, fields, conditions} of grants) {
    can(action, grantSubject(subject), buildFields(fields), buildConditions(conditions, user));
  }

  return build({
    resolveAction,
    conditionsMatcher: buildMongoQueryMatcher(
      {$and, $elemMatch, $not, $or},
      {and, elemMatch, not, or},
    ),
  });
};

export const roleHasMorePermissionsThanUser = (role, user) => {
  const userAbility = createPermissionAbility(user?.authorization?.permissions || [], [], user);

  return some(
    role?.permissions,
    (permission) => !abilityHasPermission(userAbility, permission),
  );
};

export const createUserAbility = (user) => {
  const permissions = user.authorization?.permissions || [];
  const grants = user.authorization?.grants || [];

  return createPermissionAbility(permissions, grants, user);
};

export const createRoleAbility = (role) => {
  const permissions = role.authorization?.permissions || [];
  const grants = role.authorization?.grants || [];

  return createPermissionAbility(permissions, grants);
};

export const check = (action, subject, obj, field) => ({
  action,
  subject: subject || action,
  obj,
  field,
});

export const createPermission = ({perm, grant}) =>
  grant ?
    {
      do: grant.action,
      on: grant.obj ?
        caslSubject(grantSubject(grant.subject), grant.obj) :
        grantSubject(grant.subject),
      field: grant.field,
    } :
    perm ?
      {do: perm.action, on: permissionSubject(perm.subject)} :
      UNAUTHORIZED;

export const createAuthorized = (ability) => (permission) =>
  ability.can(permission.do, permission.on, permission.field);

export const userAuthorizer = (user) => {
  const ability = createUserAbility(user);
  const permission = createPermission;
  const authorized = createAuthorized(ability);

  return {ability, permission, authorized};
};

export const roleAuthorizer = (role) => {
  const ability = createRoleAbility(role);
  const permission = createPermission;
  const authorized = createAuthorized(ability);

  return {ability, permission, authorized};
};
